import isEmpty from "lodash/isEmpty";
import { FC, useEffect, useRef, useState } from "react";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useFileDownloadMutation } from "../../../../api/services/client/clientApis";
import {
  DocumentDetailsContract,
  DocumentDetailsOthers,
} from "../../../../api/services/client/models";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiMultiSelect,
  MuiViewMorePopOver,
} from "../../../../components";
import { MuiDoubleLabelWithChip } from "../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import FileUploadPreview from "../../../../components/fileUpload/FileUploadPreview";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
  StatusBadgeType,
} from "../../../../components/statusBadge/StatusBadge";
import {
  CLIENT_DOCUMENT_TYPES,
  DOCUMENT_SITE_CORE_KEY,
  EMPTY_VALUE_DASH,
  PAGE_IDS,
  viewMorePopOverGridSize,
} from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getCountryIdsAsStringArray,
  getValueOrBlank,
  isNotEmpty,
  replaceHashWithValue,
  setApiDataToDropDownOptionForCountry,
} from "../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { isDocumentTypeContract } from "../../helper";
import { Country } from "../../modelsAndSchemas/Models";
import {
  documentSiteCoreKeys,
  uploadDocumentSiteCoreKeys,
} from "../../siteCore/SiteCoreKeys";
import { DOCUMENT_TABS_INDEX } from "../createClientDocument/CreateClientDocuments";
import { ViewDocumentsWrapper } from "./ViewDocument.style";

interface ViewDocumentProps {
  data: DocumentDetailsOthers | DocumentDetailsContract;
  open: boolean;
  onClose: () => void;
  onDocumentEdit?: (documentId: string, documentType: string) => void;
  isEditable?: boolean;
  selectedTab?: number;
}

const ViewDocument: FC<ViewDocumentProps> = ({
  data,
  open,
  onClose,
  onDocumentEdit,
  isEditable = true,
  selectedTab,
}) => {
  const { fetchLabel, commonLabels, fetchValueByKeyOfDropdown } = useSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();

  const pageId = PAGE_IDS.VIEW_DOCUMENT;
  const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };

  const { uploadDocumentTitle } = uploadDocumentSiteCoreKeys;
  const { commonARIALabel } = useNavigationSiteCore();

  const candidateStatusRef = useRef<HTMLDivElement>(null);

  const [newFile, setNewFile] = useState<File | null>(null);

  const getLabel = (key: keyof typeof uploadDocumentSiteCoreKeys) => {
    return fetchLabel(uploadDocumentTitle, uploadDocumentSiteCoreKeys[key]);
  };

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const {
    mutate: downloadFile,
    isSuccess: isSuccessOnDownload,
    data: dataOnDownload,
  } = useFileDownloadMutation();

  useEffect(() => {
    downloadFile({
      documentId: data.documentId ?? "",
      documentType:
        data.documentType === DOCUMENT_SITE_CORE_KEY.documentTypeContract
          ? CLIENT_DOCUMENT_TYPES.clientContract
          : CLIENT_DOCUMENT_TYPES.clientOthers,
    });
  }, [data]);

  useEffect(() => {
    if (dataOnDownload) {
      const file = new File([dataOnDownload.blob], data.documentName ?? "", {
        type: dataOnDownload.blob.type,
      });
      setNewFile(file);
    }
  }, [isSuccessOnDownload]);

  const getDropdownValues = (
    key: keyof typeof uploadDocumentSiteCoreKeys,
    value: string,
  ) => {
    if (isEmpty(value)) return "";
    return fetchValueByKeyOfDropdown(
      uploadDocumentTitle,
      uploadDocumentSiteCoreKeys[key],
      value,
    );
  };

  const { loggedInUser } = useGetLoggedInUserDetails();

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const getFormattedDate = (dateString: string) => {
    if (isEmpty(dateString)) return "";
    return convertToUserFormat(dateString, userCountryDateFormat);
  };

  const handleDocumentEdit = () => {
    onDocumentEdit?.(data?.id ?? "", data?.documentType ?? "");
  };

  const documentTypeContract: DocumentDetailsContract =
    data.documentType === DOCUMENT_SITE_CORE_KEY.documentTypeContract
      ? (data as DocumentDetailsContract)
      : ({} as DocumentDetailsContract);
  const documentTypeOther: DocumentDetailsOthers =
    data.documentType === DOCUMENT_SITE_CORE_KEY.documentTypeOthers
      ? (data as DocumentDetailsOthers)
      : ({} as DocumentDetailsOthers);

  const getTertiaryButtonTitle = () => {
    if (!isEditable) return undefined;

    const isContractType =
      data.documentType === DOCUMENT_SITE_CORE_KEY.documentTypeContract;

    let canEdit = true;
    if (isContractType) {
      canEdit = documentTypeContract.status !== STATUS_BADGE_TYPE.cancelled;
    } else if (selectedTab === DOCUMENT_TABS_INDEX.ARCHIVE) {
      canEdit = documentTypeOther.statusId !== STATUS_BADGE_TYPE.noLongerValid;
    }

    return canEdit ? getLabel("editDocumentButtonText") : undefined;
  };

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      title={replaceHashWithValue(
        getLabel("viewDocumentHeading"),
        getValueOrBlank(data.documentName),
      )}
      hasLargeContent
      showPrimaryButton={false}
      secondaryButtonTitle={commonLabels.close}
      tertiaryButtonTitle={getTertiaryButtonTitle()}
      onSecondaryButtonClick={onClose}
      onTertiaryButtonClick={() => handleDocumentEdit()}
      stackButtonsOnMobile={true}
      className="view-document-modal"
    >
      <ViewDocumentsWrapper>
        <AccordionHeader
          title={renderHeader(getLabel("documentDetailsHeading"))}
          showExpandCollapseIcon={false}
        >
          {data && (
            <MuiBox className="content">
              <MuiGrid container spacing={2} className="document-type">
                <MuiGrid size={gridSize}>
                  <MuiDoubleLabel
                    id={`${pageId}-${uploadDocumentSiteCoreKeys.documentType}`}
                    label={getLabel("documentType")}
                    value={fetchValueByKeyOfDropdown(
                      uploadDocumentSiteCoreKeys.uploadDocumentTitle,
                      uploadDocumentSiteCoreKeys.documentTypeList,
                      getValueOrBlank(data?.documentType),
                    )}
                  />
                </MuiGrid>
                {isDocumentTypeContract(getValueOrBlank(data?.documentType)) ? (
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.contractId}`}
                      label={getLabel("contractId")}
                      replaceEmptyWithDash
                      value={documentTypeContract?.contractId?.toString()}
                    />
                  </MuiGrid>
                ) : null}
              </MuiGrid>
              {isDocumentTypeContract(getValueOrBlank(data?.documentType)) ? (
                <MuiGrid container spacing={2} className="contract-details">
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.opportunityID}`}
                      label={getLabel("opportunityID")}
                      replaceEmptyWithDash
                      value={documentTypeContract?.opportunityId?.toString()}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.opportunityName}`}
                      label={getLabel("opportunityName")}
                      replaceEmptyWithDash
                      value={documentTypeContract?.opportunityName?.toString()}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize} className="country-multi-select">
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.countryOfOwnership}`}
                      label={getLabel("countryOfOwnership")}
                      replaceEmptyWithDash
                      value={
                        documentTypeContract?.ownershipCountry?.countryName
                      }
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.exclusivity}`}
                      label={getLabel("exclusivity")}
                      replaceEmptyWithDash
                      value={getDropdownValues(
                        "exclusivityList",
                        getValueOrBlank(documentTypeContract?.exclusivityId),
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.sowAutoRenewalClause}`}
                      label={getLabel("sowAutoRenewalClause")}
                      replaceEmptyWithDash
                      value={getDropdownValues(
                        "autoRenewalClauseList",
                        getValueOrBlank(
                          documentTypeContract?.sowAutoRenewalClauseId,
                        ),
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.sowAutoRenewalTerms}`}
                      label={getLabel("sowAutoRenewalTerms")}
                      replaceEmptyWithDash
                      value={documentTypeContract?.sowAutoRenewalTerms?.toString()}
                    />
                  </MuiGrid>

                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.sowContractEffectiveDate}`}
                      label={getLabel("sowContractEffectiveDate")}
                      replaceEmptyWithDash
                      value={getFormattedDate(
                        getValueOrBlank(
                          documentTypeContract?.sowContractEffectiveDate,
                        ),
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.sowExpirationDate}`}
                      label={getLabel("sowExpirationDate")}
                      replaceEmptyWithDash
                      value={getFormattedDate(
                        getValueOrBlank(
                          documentTypeContract?.sowExpirationDate,
                        ),
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.sowStatus}`}
                      label={getLabel("sowStatus")}
                      value={
                        documentTypeContract.status ? (
                          <StatusBadge
                            status={
                              documentTypeContract.status as StatusBadgeType
                            }
                            label={getDropdownValues(
                              "editContractStatusList",
                              documentTypeContract.status,
                            )}
                          />
                        ) : (
                          ""
                        )
                      }
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.classification}`}
                      label={getLabel("classification")}
                      replaceEmptyWithDash
                      value={getDropdownValues(
                        "classificationList",
                        getValueOrBlank(documentTypeContract?.classificationId),
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.serviceLevelAgreements}`}
                      label={getLabel("serviceLevelAgreements")}
                      replaceEmptyWithDash
                      value={getDropdownValues(
                        "serviceLevelAgreementList",
                        getValueOrBlank(
                          documentTypeContract?.serviceLevelAgreementsId,
                        ),
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.customDeliverables}`}
                      label={getLabel("customDeliverables")}
                      replaceEmptyWithDash
                      value={getDropdownValues(
                        "customDeliverablesList",
                        getValueOrBlank(
                          documentTypeContract?.customDeliverablesId,
                        ),
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.paymentTermsDropDown}`}
                      label={getLabel("paymentTermsDropDown")}
                      replaceEmptyWithDash
                      value={documentTypeContract?.paymentTerms?.name}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.durationOfContractSOWTerm}`}
                      label={getLabel("durationOfContractSOWTerm")}
                      replaceEmptyWithDash
                      value={documentTypeContract?.contractDuration?.toString()}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.engagementType}`}
                      label={getLabel("engagementType")}
                      replaceEmptyWithDash
                      value={getDropdownValues(
                        "engagementTypeList",
                        getValueOrBlank(documentTypeContract?.engagementTypeId),
                      )}
                    />
                  </MuiGrid>

                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.uploadDate}`}
                      label={getLabel("uploadDate")}
                      replaceEmptyWithDash
                      value={getFormattedDate(data.uploadDate)}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabelWithChip
                      label={getLabel("contractProductLineDropdown")}
                      replaceEmptyWithDash={
                        !(data as DocumentDetailsContract)?.productLines?.length
                      }
                      value={
                        (data as DocumentDetailsContract)?.productLines?.map(
                          (item) => ({
                            label: item.name,
                            value: item.name,
                          }),
                        ) ?? []
                      }
                      chipPopoverHeaderLabel={getLabel(
                        "contractProductLineDropdown",
                      )}
                      chipPopoverGridRef={candidateStatusRef}
                      showMoreARIALabel={commonARIALabel.showNMore}
                      removeChipNameARIALabel={commonARIALabel.removeChipName}
                      closePopoverARIALabel={commonARIALabel.closePopover}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.contractGeoScope}`}
                      label={getLabel("contractGeoScope")}
                      value={fetchValueByKeyOfDropdown(
                        documentSiteCoreKeys.documentsTitle,
                        documentSiteCoreKeys.geoScopeList,
                        documentTypeContract?.contractGeoScopeId,
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiMultiSelect
                      label={getLabel("contractGeoScopeDetail")}
                      showChip={true}
                      options={setApiDataToDropDownOptionForCountry(
                        documentTypeContract?.contractGeoScopeCountries as Country[],
                      )}
                      hidden
                      selectedValues={getCountryIdsAsStringArray(
                        documentTypeContract?.contractGeoScopeCountries as Country[],
                      )}
                      isChipEditable={false}
                      showDropdown={false}
                      dialogWidth={376}
                      dialogTitle={getLabel("contractGeoScopeDetail")}
                      selectPlaceHolder={commonLabels.selectPlaceHolder}
                      searchPlaceHolder={commonLabels.search}
                      selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                      doneText={commonLabels.done}
                      allARIAProps={multiSelectARIALabels}
                    />
                  </MuiGrid>
                </MuiGrid>
              ) : (
                <MuiGrid container spacing={2} className="other-details">
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.status}`}
                      label={getLabel("status")}
                      value={
                        <StatusBadge
                          status={
                            documentTypeOther?.statusId ??
                            STATUS_BADGE_TYPE.active
                          }
                          label={getDropdownValues(
                            "editContractStatusList",
                            documentTypeOther?.statusId ??
                              STATUS_BADGE_TYPE.active,
                          )}
                        />
                      }
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.uploadDate}`}
                      label={getLabel("uploadDate")}
                      replaceEmptyWithDash
                      value={getFormattedDate(
                        getValueOrBlank(documentTypeOther?.uploadDate),
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={gridSize}>
                    <MuiDoubleLabel
                      id={`${pageId}-${uploadDocumentSiteCoreKeys.effectiveDate}`}
                      label={getLabel("effectiveDate")}
                      replaceEmptyWithDash
                      value={getFormattedDate(
                        getValueOrBlank(documentTypeOther?.effectiveDate),
                      )}
                    />
                  </MuiGrid>
                </MuiGrid>
              )}
              <MuiGrid container>
                <MuiGrid size={viewMorePopOverGridSize} className="notes">
                  <MuiDoubleLabel
                    id={`${pageId}-${documentSiteCoreKeys.notes}`}
                    label={commonLabels.notes}
                    value={
                      isNotEmpty(data?.notes) ? (
                        <MuiViewMorePopOver
                          anchorText={commonLabels.viewMore}
                          message={data.notes}
                          placement="bottom-end"
                        />
                      ) : (
                        EMPTY_VALUE_DASH
                      )
                    }
                  />
                </MuiGrid>
              </MuiGrid>
            </MuiBox>
          )}
        </AccordionHeader>
        <AccordionHeader
          title={renderHeader(getLabel("attachmentHeading"))}
          showExpandCollapseIcon={false}
        >
          <MuiBox className="content file-upload">
            {newFile && (
              <FileUploadPreview showCloseIcon={false} file={newFile} />
            )}
          </MuiBox>
        </AccordionHeader>
        <MuiBox />
      </ViewDocumentsWrapper>
    </MuiModal>
  );
};
export default ViewDocument;
